(() => {
  'use strict';

  const selector = '[data-cha-guided-action]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const registered = new WeakSet();
  const introComplete = new WeakSet();
  const interactionComplete = new WeakSet();
  const queue = [];
  let active = null;
  let activeTimer = 0;

  const removeAnimationClasses = element => {
    element.classList.remove('is-cha-guided-action-intro', 'is-cha-guided-action-replay');
  };

  const finishActive = () => {
    if (!active) return;
    window.clearTimeout(activeTimer);
    removeAnimationClasses(active.element);
    if (active.kind === 'intro') introComplete.add(active.element);
    active = null;
    runNext();
  };

  const runNext = () => {
    if (active || reducedMotion.matches) return;
    const next = queue.shift();
    if (!next || !next.element.isConnected || next.element.hidden) {
      if (queue.length) runNext();
      return;
    }

    active = next;
    const className = next.kind === 'intro' ? 'is-cha-guided-action-intro' : 'is-cha-guided-action-replay';
    removeAnimationClasses(next.element);
    void next.element.offsetWidth;
    next.element.classList.add(className);
    activeTimer = window.setTimeout(finishActive, next.kind === 'intro' ? 5900 : 3100);
  };

  const enqueue = (element, kind) => {
    if (reducedMotion.matches || !element.isConnected) return;
    if (active?.element === element || queue.some(item => item.element === element && item.kind === kind)) return;
    queue.push({ element, kind });
    runNext();
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.55) return;
        observer.unobserve(entry.target);
        enqueue(entry.target, 'intro');
      });
    }, { threshold: [0.55] })
    : null;

  const register = element => {
    if (registered.has(element)) return;
    registered.add(element);

    if (reducedMotion.matches) {
      element.classList.add('is-cha-guided-action-reduced');
      return;
    }

    if (observer) observer.observe(element);
    else enqueue(element, 'intro');

    let hoverTimer = 0;
    element.addEventListener('pointerenter', () => {
      if (!introComplete.has(element) || interactionComplete.has(element)) return;
      hoverTimer = window.setTimeout(() => {
        interactionComplete.add(element);
        enqueue(element, 'replay');
      }, 180);
    });
    element.addEventListener('pointerleave', () => window.clearTimeout(hoverTimer));
    element.addEventListener('focus', () => {
      if (!introComplete.has(element) || interactionComplete.has(element)) return;
      interactionComplete.add(element);
      enqueue(element, 'replay');
    });
    element.addEventListener('animationend', event => {
      if (event.animationName === 'cha-guided-action-orbit' && active?.element === element) finishActive();
    });
  };

  const registerWithin = root => {
    if (root.matches?.(selector)) register(root);
    root.querySelectorAll?.(selector).forEach(register);
  };

  const syncReducedMotion = () => {
    document.querySelectorAll(selector).forEach(element => {
      removeAnimationClasses(element);
      element.classList.toggle('is-cha-guided-action-reduced', reducedMotion.matches);
      if (!reducedMotion.matches && registered.has(element) && !introComplete.has(element)) {
        if (observer) observer.observe(element);
        else enqueue(element, 'intro');
      }
    });
    if (reducedMotion.matches) {
      queue.length = 0;
      active = null;
      window.clearTimeout(activeTimer);
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    registerWithin(document);
    const observationRoot = document.body || document.documentElement;
    if (!observationRoot) return;
    new MutationObserver(mutations => {
      mutations.forEach(mutation => mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) registerWithin(node);
      }));
    }).observe(observationRoot, { childList: true, subtree: true });
  });

  reducedMotion.addEventListener?.('change', syncReducedMotion);
})();
